import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  skeletonRecipe,
  skeletonSweepRecipe,
  type SkeletonRecipeProps,
} from '../recipes/feedback.recipe';

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    SkeletonRecipeProps {}

/**
 * A grey block the shape of what is loading.
 *
 *   <Skeleton className="h-4 w-24" />              a line of text
 *   <Skeleton shape="circle" className="size-10" />  an avatar
 *   <Skeleton shape="rect" className="aspect-[4/3]" animate />
 *
 * Draw the screen's own shape with these, empty, so the swap to real content
 * is a fill rather than a jump. Hidden from assistive technology: the parent
 * should carry `role="status"` and a label, once, rather than fifty blocks
 * each announcing nothing.
 */
export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  function Skeleton({ shape, tone, animate, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(skeletonRecipe({ shape, tone, animate }), className)}
        {...props}
      />
    );
  }
);

export type SkeletonSweepProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * A band of light gliding across a screen built from Skeletons.
 *
 *   <StudioLayout role="status" aria-label="Loading">
 *     …skeleton panes…
 *     <SkeletonSweep />
 *   </StudioLayout>
 *
 * Drop it inside the frame the skeleton fills — one that is positioned and
 * clips overflow. Decorative, so hidden from assistive technology; under
 * reduced motion it does not appear at all.
 */
export const SkeletonSweep = React.forwardRef<
  HTMLDivElement,
  SkeletonSweepProps
>(function SkeletonSweep({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(skeletonSweepRecipe(), className)}
      {...props}
    />
  );
});
